import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, Switch, TablePagination, TextField, Typography } from '@mui/material';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useDebounce } from 'use-lodash-debounce';
import { UnsubscribeLead, ViewLeadsForExistingAudience } from 'src/redux/slices/lead';
import { textFieldStyle } from 'src/styles/NewLead';
import Iconify from '../Iconify';
import { useDispatch, useSelector } from '../../redux/store';
import { useTable } from '../../hooks';
import CampaignAudiencePreviewTable from './CampaignAudiencePreviewTable';
import { formControlLabelStyle } from '../../styles/DuplicateLeadStyle';
import { TableLoading, LoadingScreen } from '../loading';


const CampaignAudiencePreviewPopup = ({ open, onClose, filterForExistingLeads }) => {

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const { existingAudienceLeadList: { results: existingAudienceLeadListResults, count: existingAudienceCount } } = useSelector((state) => state.lead);

    const [search, setSearch] = useState('');
    const [filteredData, setFilteredData] = useState([])

    const isCsvData = filterForExistingLeads?.csvData || false;

    const debouncedValue = useDebounce(search, 1000);

    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch();

    const {
        dense,
        page,
        rowsPerPage,
        onChangeDense,
        onChangePage,
        onChangeRowsPerPage,
    } = useTable();

    const firstRender = useRef(true);

    useEffect(()=>{
        if (isCsvData){
            setFilteredData(existingAudienceLeadListResults)
        } else {
            setFilteredData([])
        }
    }, [existingAudienceLeadListResults, filterForExistingLeads])

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (isCsvData) {
            setFilteredData([]);
            const filteredData = existingAudienceLeadListResults.filter((row) => {
                const fullName = `${row?.first_name || ''} ${row?.last_name || ''}`;
                const email = row?.lead_contact?.email || '';
                return fullName.toLowerCase().includes(debouncedValue.toLowerCase()) || email.toLowerCase().includes(debouncedValue.toLowerCase());
            });
            setFilteredData(filteredData);
            return;
        }
        const fieldParams = {
            limit: rowsPerPage,
            offset: rowsPerPage * page
        };
        if (debouncedValue) {
            dispatch(ViewLeadsForExistingAudience(practiceId, fieldParams, filterForExistingLeads, debouncedValue));
        } else {
            dispatch(ViewLeadsForExistingAudience(practiceId, fieldParams, filterForExistingLeads));
        }
    }, [debouncedValue]);

    const handleChangePage = (event, newPage) => {
        onChangePage(event, newPage);
        const fieldParams = {
            limit: rowsPerPage,
            offset: rowsPerPage * newPage
        };
        dispatch(ViewLeadsForExistingAudience(practiceId, fieldParams, filterForExistingLeads));
    };

    const handleChangePerPage = (event) => {
        onChangeRowsPerPage(event);
        const fieldParams = {
            limit: event?.target?.value,
            offset: 0
        };
        dispatch(ViewLeadsForExistingAudience(practiceId, fieldParams, filterForExistingLeads));
    };

    const shouldLoading = () => isCsvData ? (filteredData.length === 0 && !search && 'No Found') : (existingAudienceLeadListResults?.length === 0 && !search && 'No Found');

    const handleSuccessLeadUnsubscribe = (data) => {
        if (data.success) {
            if (!isCsvData){
                const fieldParams = {
                    limit: rowsPerPage,
                    offset: rowsPerPage * page
                };
                dispatch(ViewLeadsForExistingAudience(practiceId, fieldParams, filterForExistingLeads));
            }

            enqueueSnackbar(data.message || '', {
                variant: data.success ? 'success' : 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });

        }
    }
    const handleUnsubscribeLead = (row) => {
        const dataEmail = { email: row?.lead_contact?.email || "" };
        dispatch(UnsubscribeLead(practiceId, dataEmail, handleSuccessLeadUnsubscribe));

    };

    const handleSearch = (data) => {
        setSearch(data?.trim());
    };

    return <Dialog fullWidth maxWidth={'lg'} open={open} onClose={onClose} disableScrollLock>
        <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant='h4' >Audience preview </Typography>
                <Box sx={{ width: "40%", paddingBottom: "30px" }}>
                    <TextField
                        fullWidth
                        onChange={(event) => handleSearch(event.target.value)}
                        value={search}
                        sx={textFieldStyle}
                        placeholder='Search lead name or email'
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Iconify
                                        icon={'eva:search-fill'}
                                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

        </DialogTitle>
        <DialogContent>
            <Box sx={{ position: 'relative' }}>
                {(!existingAudienceLeadListResults?.length && !search) ? <Box sx={{ minHeight: "400px", width: "100%", paddingTop: "200px" }}><LoadingScreen /></Box> : (
                    <>
                        <TableLoading tableData={shouldLoading()} pageCount={shouldLoading()} type={'table'} />
                        <Box>
                            <CampaignAudiencePreviewTable dense={dense} handleUnsubscribeLead={handleUnsubscribeLead} filteredData={filteredData}/>
                        </Box>
                        <Box sx={{ position: 'relative', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {existingAudienceCount > 0 && (
                                    <FormControlLabel
                                        control={<Switch checked={dense} onChange={onChangeDense} />}
                                        label="Dense"
                                        sx={formControlLabelStyle}
                                    />
                                )}
                                {existingAudienceCount > 10 && !isCsvData && (
                                    <TablePagination
                                        rowsPerPageOptions={[10, 25]}
                                        component="div"
                                        count={existingAudienceCount}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(event, newPage) => handleChangePage(event, newPage)}
                                        onRowsPerPageChange={(event) => handleChangePerPage(event)}
                                    />
                                )}
                                {isCsvData && <Typography  sx={{mr: 3, fontSize:"16px"}} >Row Count : {filteredData.length}</Typography>}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </DialogContent>
        <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
                Close
            </Button>
        </DialogActions>
    </Dialog >;
};

export default CampaignAudiencePreviewPopup;

CampaignAudiencePreviewPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    filterForExistingLeads: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.oneOf([null])
    ])
};
